import React, { useEffect, useState } from 'react'
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  Timestamp,
  getDoc,
} from 'firebase/firestore'
import { db } from '../../firebaseConfig'

// SERVICE
import { approveMissionClaim } from '../../services/approveMissionClaim'
import { exportMissionClaims } from '../../services/exportMissionClaims'
import { rejectMissionClaim } from '../../services/rejectMissionClaim' // ✨ BARU: Import file reject

export default function MissionClaimsPage() {
  const [claims, setClaims] = useState([])
  const [loadingId, setLoadingId] = useState(null)
  const [loading, setLoading] = useState(false)

  // =====================================================
  // LOAD PENDING CLAIMS
  // =====================================================
  const loadClaims = async () => {
    const q = query(
      collection(db, 'mission_claim_logs'),
      where('approved', '==', false),
      where('rejected', '==', false)
    )

    const snap = await getDocs(q)
    
    // ✨ PERBAIKAN: Mengambil Nama Mitra dan Judul Misi
    const enrichedClaims = await Promise.all(
      snap.docs.map(async (d) => {
        const x = d.data()

        // 1. Cari Nama Mitra
        let mitraName = 'Nama Tidak Ditemukan'
        if (x.mitraUid && x.mitraType) {
          const colMitra = x.mitraType === 'cleaner' ? 'cleaners' : 'therapists'
          try {
            const mSnap = await getDoc(doc(db, colMitra, x.mitraUid))
            if (mSnap.exists()) mitraName = mSnap.data().name || mitraName
          } catch (e) { console.error(e) }
        }

        // 2. Cari Judul Misi
        let missionTitle = 'Misi Tidak Ditemukan'
        if (x.missionId && x.missionType) { 
          // Di Flutter, missionType digunakan untuk menyimpan scope (daily/weekly)
          const colMission = x.missionType === 'daily' ? 'missions_daily' : 'missions_weekly'
          try {
            const msnSnap = await getDoc(doc(db, colMission, x.missionId))
            if (msnSnap.exists()) missionTitle = msnSnap.data().title || missionTitle
          } catch (e) { console.error(e) }
        }

        return { 
          id: d.id, 
          ...x, 
          mitraName, 
          missionTitle 
        }
      })
    )

    setClaims(enrichedClaims)
  }

  useEffect(() => {
    loadClaims()
  }, [])

  // =====================================================
  // APPROVE
  // =====================================================
  const approve = async (claim) => {
    setLoadingId(claim.id)
    try {
      await approveMissionClaim({
        claimId: claim.id,
        mitraId: claim.mitraUid, // ✨ PERBAIKAN: Menyesuaikan nama dari Flutter
        mitraType: claim.mitraType,
        missionId: claim.missionId,
        missionScope: claim.missionType, // ✨ PERBAIKAN: Flutter mengirim daily/weekly di field ini
        bonus: claim.bonus,
      })
      await loadClaims()
    } catch (e) {
      alert(e.message)
    } finally {
      setLoadingId(null)
    }
  }

  // =====================================================
  // REJECT
  // =====================================================
  const reject = async (claimId, reason) => {
    if (!window.confirm('Tolak klaim ini?')) return

    setLoadingId(claimId)
    try {
      // ✨ PERBAIKAN: Menggunakan file service yang sudah Anda buat
      await rejectMissionClaim(claimId, reason) 
      await loadClaims()
    } catch (e) {
      alert(e.message)
    } finally {
      setLoadingId(null)
    }
  }

  // =====================================================
  // EXPORT EXCEL
  // =====================================================
  const exportExcel = async () => {
    setLoading(true)
    try {
      await exportMissionClaims()
    } finally {
      setLoading(false)
    }
  }

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <div>
      {/* HEADER */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-blue-400">
          📥 Approval Klaim Misi
        </h2>

        <button
          onClick={exportExcel}
          disabled={loading}
          className="px-4 py-2 bg-green-600 rounded text-sm"
        >
          Export Excel
        </button>
      </div>

      {/* LIST */}
      {claims.map((c) => (
        <div
          key={c.id}
          className="p-4 mb-3 bg-gray-800 border border-gray-700 rounded-lg flex justify-between items-center hover:bg-gray-750 transition"
        >
          <div>
            {/* ✨ Menampilkan Nama Mitra */}
            <div className="font-bold text-white text-lg flex items-center gap-2">
              {c.mitraName} 
              <span className="text-[10px] font-normal px-2 py-0.5 bg-gray-700 text-gray-300 rounded-full">
                {c.mitraType.toUpperCase()}
              </span>
            </div>
            
            {/* ✨ Menampilkan Judul Misi */}
            <div className="text-sm text-blue-300 mt-1 font-semibold">
              Misi: {c.missionTitle} <span className="text-xs text-gray-500 ml-1">({c.missionType === 'daily' ? 'Harian' : 'Mingguan'})</span>
            </div>
            
            {/* Nominal Bonus */}
            <div className="text-sm text-gray-400 mt-1">
              Reward Bonus: <span className="text-green-400 font-bold">Rp {c.bonus?.toLocaleString('id-ID')}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              disabled={loadingId === c.id}
              onClick={() => approve(c)}
              className="px-4 py-2 bg-green-600 rounded text-white text-sm"
            >
              Approve
            </button>

            <button
              onClick={() => reject(c.id, 'Data tidak valid')}
              className="px-3 py-2 bg-red-600 rounded text-sm"
            >
              Reject
            </button>
          </div>
        </div>
      ))}

      {/* EMPTY */}
      {claims.length === 0 && (
        <div className="text-gray-400">Tidak ada klaim misi pending</div>
      )}
    </div>
  )
}
